/* eslint-disable react/require-default-props */
import _ from 'underscore';
import React, {
  useContext, useMemo, useState, useEffect,
} from 'react';
import { DirectionalHint } from '@fluentui/react';
import { withPreference } from '@bingads-webui-react/preference-context';
import { mergeClasses } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withUserConsumer } from '@bingads-webui-react/user-context';
import { withScope } from '@bingads-webui-react/scope-service';
import { SideNavigation, SideNavigationItemSelectionMode, SideNavigationModes } from '@bingads-webui-fluent-component/navigation';
import { ToolsDialog } from '@bingads-webui-react/tools-dialog';
import { ToolsPanel } from '@bingads-webui-react/tools-panel';
import { Scenario, Activity, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { LayoutSize, useLayoutResizeObserver } from '@bingads-webui-fluent-component/layout-size';
import { withStateStore } from '@bingads-webui/with-state-store';
import { StateStore } from '@bingads-webui/state-store';
import { TeachingBubble } from '@bingads-webui-campaign-react/popup-components';
import { getParameter } from '@bingads-webui/url-util';

import {
  I18n, Scope, SitemapItem, UserContext, IActivityContext,
} from './types';
import { getNavigationItemsConfig } from './utils/primary-navigation';
import { getToolsDialogConfig } from './utils/tools-dialog';
import { processSitemap } from './utils/preference-processor';
import { usePrimaryNavigationStyles } from './styles';

const ToolsDialogActivity = 'tools-dialog';
const L1PinStore = new StateStore({});

interface PerfMarker {
  willInit(): void;
  willFetchData(): void;
  willRender(): void;
  done(): void;
  createChild(name: string): PerfMarker;
}

interface PrimaryNavigationInternalProps {
  scope: Scope;
  siteMap: SitemapItem[];
  userContext: UserContext;
  appPerfMarker?: PerfMarker;
  preferenceService: any;
  store: any;
  permissions: any;
  toolsPanelSiteMapProcessors?: any[];
}

const getModeByLayoutSize = (layoutSize: LayoutSize): SideNavigationModes => (
  _.contains([LayoutSize.S, LayoutSize.M], layoutSize)
    ? SideNavigationModes.IconOnly
    : SideNavigationModes.Detailed
);

const getPerMarker = (appPerfMarker: PerfMarker) => {
  if (appPerfMarker == null) {
    return null;
  }
  const marker = appPerfMarker.createChild('primary-nav-perf');
  marker.willInit();
  return marker;
};

const PrimaryNavigationInternal: React.FC<PrimaryNavigationInternalProps> = (props) => {
  const {
    scope,
    siteMap: originalSiteMap,
    userContext,
    appPerfMarker,
    preferenceService,
    store,
    permissions,
    toolsPanelSiteMapProcessors,
  } = props;
  const [perfMarker] = useState(() => getPerMarker(appPerfMarker));
  const activityContext: IActivityContext = useContext(ActivityContext);
  const i18n: I18n = useContext(I18nContext);
  const classes = usePrimaryNavigationStyles();
  const [layoutSize, setLayoutSize] = useState<LayoutSize>(LayoutSize.S);
  const mode = useMemo(() => getModeByLayoutSize(layoutSize), [layoutSize]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showTeachingBubble, setShowTeachingBubble] = useState(true);
  const siteMap = useMemo(
    () => processSitemap(originalSiteMap, preferenceService, store, activityContext, permissions),
    [originalSiteMap, preferenceService, store, activityContext, store.state],
  );
  const ise2etest = getParameter('ise2etest') === 'true';
  const showForTest = getParameter('showNavRefreshTeachingBubble') === 'true';
  const hideBubbleForTest = ise2etest && !showForTest;
  const isNavRefresh2Enabled = permissions && permissions.dynamic && permissions.dynamic.NavigationRefreshTools;

  const unpinnedItemSelected = useMemo(() => {
    const selectedSiteMap = _.find(siteMap, (item) => !!item.selected);

    if (selectedSiteMap && (!selectedSiteMap.pinned && !selectedSiteMap.fixed)) {
      return true;
    }
    return false;
  }, [siteMap]);

  const hideTeachingBubble = () => {
    setShowTeachingBubble(false);
  };

  useLayoutResizeObserver(setLayoutSize);

  const navigationItems = useMemo(() => getNavigationItemsConfig({
    i18n,
    siteMap,
    preferenceService,
    scope,
    userContext,
    activityContext,
    permissions,
    onOpenAllTools: () => {
      activityContext.signal('open tools dialog', ToolsDialogActivity);
      setDialogOpen(true);
    },
  }), [i18n, siteMap, scope, userContext]);

  const onDialogItemClick = (item) => {
    activityContext.signal(`tools item clicked: ${JSON.stringify(item)}`, ToolsDialogActivity);
    setDialogOpen(false);
  };

  const dialogConfig = useMemo(() => getToolsDialogConfig({
    i18n,
    siteMap,
    scope,
    userContext,
    permissions,
    onClick: onDialogItemClick,
  }), [i18n, siteMap, scope, userContext]);

  useEffect(() => {
    if (perfMarker) {
      perfMarker.done();
    }
  }, [perfMarker]);

  return (
    <div className={mergeClasses(classes.root, mode === SideNavigationModes.Detailed ? classes.rootDetailed : classes.rootIconOnly)}>
      <div className={classes.container}>
        <SideNavigation
          className={mergeClasses(classes.menuItem, mode === SideNavigationModes.Detailed ? classes.menuItemDetailed : classes.menuItemIconOnly)}
          mode={mode}
          items={navigationItems}
          config={{ selectionMode: SideNavigationItemSelectionMode.ItemId }}
          reserveSelectedTabSpace={false}
          size="small"
        />
      </div>
      {!isNavRefresh2Enabled && (
        <ToolsDialog
          config={dialogConfig}
          open={dialogOpen}
          onClose={() => {
            activityContext.signal('close tools dialog', ToolsDialogActivity);
            setDialogOpen(false);
          }}
        />
      )}
      {isNavRefresh2Enabled && (
        <ToolsPanel
          config={dialogConfig}
          open={dialogOpen}
          onClose={() => {
            activityContext.signal('close tools dialog', ToolsDialogActivity);
            setDialogOpen(false);
          }}
          overlayMargins={{ marginLeft: '70', marginTop: '60' }}
          userContext={userContext}
          onClick={onDialogItemClick}
          processorSet={toolsPanelSiteMapProcessors}
        />
      )}

      {unpinnedItemSelected && showTeachingBubble && !hideBubbleForTest && (
        <TeachingBubble
          featureName="pin-primary-menu-item"
          title={i18n.getString(_TL_('Pin tools for quick access'))}
          content={
          i18n.getString(_TL_('Is this a tool you want to get back to easily? Hover over the tab to access the pin action to keep it here. Not something you want to use often? Don\'t worry, it will disappear when you leave the tool.'))
        }
          primaryButtonText={i18n.getString(_TL_('Got it'))}
          targetElement=".nav-item-unpinned-selected"
          directionalHint={DirectionalHint.rightCenter}
          isSecondaryButtonHidden
          onDone={hideTeachingBubble}
          onClose={hideTeachingBubble}
        />
      )}
    </div>
  );
};

const PrimaryNavigationWithScenarioActivityContext: React.FC<any> = (props) => (
  <Scenario name="primary-navigation">
    <Activity name="primary-navigation">
      <PrimaryNavigationInternal {...props} />
    </Activity>
  </Scenario>
);

export const PrimaryNavigation = withUserConsumer(withScope(withStateStore(withPreference(PrimaryNavigationWithScenarioActivityContext), L1PinStore)));
